/**
 * Field — composes Label + control + Description + ErrorMessage with
 * automatic ARIA wiring (id / aria-describedby / aria-invalid / required).
 *
 * Built on Base UI <Field>. Use one Field per logical form control.
 *
 * Example:
 *   <Field>
 *     <Field.Label>Email</Field.Label>
 *     <Field.Control render={<Input type="email" />} />
 *     <Field.Description>We'll never share your email.</Field.Description>
 *     <Field.Error />
 *   </Field>
 *
 * The convenience <FieldShorthand> wraps the most common pattern:
 *
 *   <FieldShorthand label="Email" description="..." error="...">
 *     <Input type="email" />
 *   </FieldShorthand>
 */
import * as React from "react"
import { Field as BaseField } from "@base-ui-components/react/field"
import { cn } from "@lib/cn"

const Root = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseField.Root>
>(({ className, ...props }, ref) => (
  <BaseField.Root
    ref={ref}
    className={cn("grid gap-1 mb-3", className)}
    {...props}
  />
))
Root.displayName = "Field.Root"

const FieldLabel = React.forwardRef<
  HTMLLabelElement,
  React.ComponentPropsWithoutRef<typeof BaseField.Label>
>(({ className, ...props }, ref) => (
  <BaseField.Label
    ref={ref}
    className={cn(
      "text-sm font-normal leading-tight text-webex-gray-950 dark:text-webex-gray-50 select-none",
      "data-[required]:after:content-['*'] data-[required]:after:ml-0.5 data-[required]:after:text-webex-red-600",
      className,
    )}
    {...props}
  />
))
FieldLabel.displayName = "Field.Label"

const FieldDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof BaseField.Description>
>(({ className, ...props }, ref) => (
  <BaseField.Description
    ref={ref}
    className={cn("text-xs text-webex-gray-600 dark:text-webex-gray-400", className)}
    {...props}
  />
))
FieldDescription.displayName = "Field.Description"

const FieldError = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof BaseField.Error>
>(({ className, ...props }, ref) => (
  <BaseField.Error
    ref={ref}
    className={cn("text-xs text-webex-red-700 dark:text-webex-red-400", className)}
    {...props}
  />
))
FieldError.displayName = "Field.Error"

const FieldControl = BaseField.Control
const FieldValidity = BaseField.Validity

export const Field = Object.assign(Root, {
  Root,
  Label: FieldLabel,
  Description: FieldDescription,
  Error: FieldError,
  Control: FieldControl,
  Validity: FieldValidity,
})

export interface FieldShorthandProps {
  label?: React.ReactNode
  description?: React.ReactNode
  error?: React.ReactNode
  required?: boolean
  children: React.ReactNode
  className?: string
}

/** Convenience wrapper for the most common Field pattern. */
export function FieldShorthand({
  label,
  description,
  error,
  required,
  children,
  className,
}: FieldShorthandProps) {
  return (
    <Field.Root className={className} invalid={Boolean(error)}>
      {label && (
        <Field.Label data-required={required ? "" : undefined}>
          {label}
        </Field.Label>
      )}
      {children}
      {description && <Field.Description>{description}</Field.Description>}
      {error && <Field.Error match>{error}</Field.Error>}
    </Field.Root>
  )
}
